import React, { useState, useEffect } from 'react';
import { 
  FaUsers, 
  FaPlus,
  FaEye
} from 'react-icons/fa';

const AdminDashboard = ({ adminApis, adminData, setCurrentView }) => {
  const [stats, setStats] = useState({
    totalDealers: 0,
    activeDealers: 0,
    inactiveDealers: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!adminApis) {
        setLoading(false);
        return;
      }

      try {
        setError(null);
        console.log('Fetching dealer data...');
        
        // Fetch dealers for statistics
        const dealers = await adminApis.getDealers();
        console.log('Dealers data:', dealers);

        setStats({
          totalDealers: dealers.length,
          activeDealers: dealers.filter(d => d.status !== 'inactive').length,
          inactiveDealers: dealers.filter(d => d.status === 'inactive').length
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError('Failed to load dashboard data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [adminApis]);

  const handleAddDealer = () => {
    if (setCurrentView) {
      setCurrentView('add-dealer');
    }
  };

  const handleManageDealers = () => {
    if (setCurrentView) {
      setCurrentView('dealers');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600 mt-1">
            Welcome back{adminData?.user?.username ? `, ${adminData.user.username}` : ''}! Manage your dealer network.
          </p>
          {adminData?.store?.storeName && (
            <p className="text-sm text-gray-500 mt-1">
              Store: {adminData.store.storeName}
            </p>
          )}
        </div>
        <div className="text-sm text-gray-500">
          Last updated: {new Date().toLocaleString()}
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="bg-blue-100 p-3 rounded-lg">
              <FaUsers className="text-blue-600 text-xl" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Total Dealers</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalDealers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="bg-green-100 p-3 rounded-lg">
              <FaUsers className="text-green-600 text-xl" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Active Dealers</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeDealers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="bg-red-100 p-3 rounded-lg">
              <FaUsers className="text-red-600 text-xl" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Inactive Dealers</p>
              <p className="text-2xl font-bold text-gray-900">{stats.inactiveDealers}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Add New Dealer</h3>
              <p className="text-gray-600 text-sm mt-1">Register a new dealer to the platform</p>
            </div>
            <FaPlus className="text-blue-500 text-2xl" />
          </div>
          <button 
            onClick={handleAddDealer}
            className="mt-4 w-full bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors duration-200"
          >
            Add Dealer
          </button>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Manage Dealers</h3>
              <p className="text-gray-600 text-sm mt-1">View and manage your dealer network</p>
            </div>
            <FaEye className="text-green-500 text-2xl" />
          </div>
          <button 
            onClick={handleManageDealers}
            className="mt-4 w-full bg-green-500 text-white py-2 px-4 rounded-lg hover:bg-green-600 transition-colors duration-200"
          >
            Manage Dealers
          </button>
        </div>
      </div>

      {/* Getting Started */}
      {stats.totalDealers === 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="text-center">
            <FaUsers className="mx-auto h-12 w-12 text-blue-400 mb-4" />
            <h3 className="text-lg font-medium text-blue-900 mb-2">Get Started with Dealer Management</h3>
            <p className="text-blue-700 mb-4">
              You haven't added any dealers yet. Start by adding your first dealer to begin managing your network.
            </p>
            <button 
              onClick={handleAddDealer}
              className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors duration-200 flex items-center space-x-2 mx-auto"
            >
              <FaPlus className="w-4 h-4" />
              <span>Add Your First Dealer</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard; 